import { FormEvent, useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { useFeedback } from '../../hooks/useFeedback';
import type { ExpenseCostSplit } from '../../api';
import {
  FUEL_TYPES,
  refuelCost,
  type FlightRefuelDraft,
  type FuelType,
} from '../../utils/flightForm';
import {
  emptyFuelSplit,
  fuelSplitError,
  type FuelSplitState,
  type HourFlight,
} from '../../utils/expenseSplit';
import { expenseSplitLabel, fuelSplitsForActor } from '../../utils/expenseTypes';
import ExpenseSplitFields from '../ExpenseSplitFields/ExpenseSplitFields';
import ModalOverlay from '../modal/ModalOverlay';
import ModalPortal from '../modal/ModalPortal';
import '../modal/ModalBase.css';
import './AddRefuelModal.css';

type Props = {
  defaultLocation: string;
  initial?: FlightRefuelDraft | null;
  onClose: () => void;
  onSave: (item: Omit<FlightRefuelDraft, 'key'>) => void;
  date: string;
  defaultPayerId?: number;
  payerOptions: { id: number; name: string }[];
  lockedHourFlight?: (HourFlight & { label?: string }) | null;
  allowFullSplit?: boolean;
};

const AddRefuelModal = ({
  defaultLocation,
  initial = null,
  onClose,
  onSave,
  date,
  defaultPayerId = 0,
  payerOptions,
  lockedHourFlight = null,
  allowFullSplit = false,
}: Props) => {
  const { currentUserId } = useAuth();
  const { showError, feedbackNode } = useFeedback();
  const selfPayerId = currentUserId || defaultPayerId;
  const splitLocked =
    !allowFullSplit &&
    Boolean(initial?.costSplit) &&
    initial?.costSplit !== 'personal';
  const initialSplit = initial
    ? {
        costSplit:
          allowFullSplit || splitLocked
            ? initial.costSplit || 'personal'
            : 'personal',
        manualShares: initial.manualShares,
        flightIds: initial.flightIds,
        payerUserId:
          allowFullSplit || splitLocked
            ? initial.payerUserId || defaultPayerId
            : selfPayerId,
      }
    : emptyFuelSplit(allowFullSplit ? defaultPayerId : selfPayerId);
  const [liters, setLiters] = useState(
    initial ? String(initial.liters) : ''
  );
  const [fuelType, setFuelType] = useState<FuelType>(
    initial?.fuel_type || '100LL'
  );
  const [price, setPrice] = useState(
    initial ? String(initial.price_per_liter) : ''
  );
  const [location, setLocation] = useState(
    initial?.location || defaultLocation
  );
  const [costSplit, setCostSplit] = useState<ExpenseCostSplit>(
    initialSplit.costSplit
  );
  const [manualShares, setManualShares] = useState(initialSplit.manualShares);
  const [flightIds, setFlightIds] = useState(initialSplit.flightIds);
  const [payerUserId, setPayerUserId] = useState(
    initialSplit.payerUserId || defaultPayerId
  );

  const litersNum = Number(String(liters).replace(',', '.'));
  const priceNum = Number(String(price).replace(',', '.'));
  const lineTotal =
    Number.isFinite(litersNum) &&
    litersNum > 0 &&
    Number.isFinite(priceNum) &&
    priceNum >= 0
      ? refuelCost(litersNum, priceNum)
      : null;
  const splitVisible = (lineTotal || 0) > 0;
  const splitState: FuelSplitState = {
    costSplit: allowFullSplit || splitLocked ? costSplit : 'personal',
    manualShares,
    flightIds,
    payerUserId: allowFullSplit || splitLocked
      ? payerUserId || defaultPayerId
      : selfPayerId,
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!Number.isFinite(litersNum) || litersNum <= 0) {
      showError('Укажите количество литров');
      return;
    }
    if (!Number.isFinite(priceNum) || priceNum < 0) {
      showError('Укажите цену за литр');
      return;
    }
    if (splitVisible) {
      const error = fuelSplitError(splitState, true);
      if (error) {
        showError(error);
        return;
      }
    }
    onSave({
      liters: litersNum,
      fuel_type: fuelType,
      price_per_liter: priceNum,
      location: location.trim(),
      ...splitState,
      payerUserId: splitState.payerUserId,
    });
  };

  return (
    <ModalPortal>
      <ModalOverlay onClose={onClose} className="modal-overlay refuel-modal-overlay">
        {feedbackNode}
        <form
          className="modal refuel-modal"
          onClick={(e) => e.stopPropagation()}
          onSubmit={handleSubmit}
        >
          <h3>{initial ? 'Заправка' : 'Добавить заправку'}</h3>
          <div className="modal-body">
            <label>Количество литров</label>
            <input
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={liters}
              onChange={(e) => setLiters(e.target.value)}
              placeholder="0"
            />
            <label>Вид топлива</label>
            <select
              value={fuelType}
              onChange={(e) => setFuelType(e.target.value as FuelType)}
            >
              {FUEL_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
            <label>Цена за литр</label>
            <input
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="0"
            />
            <label>Где</label>
            <input
              type="text"
              maxLength={200}
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Место заправки"
            />
            {lineTotal ? (
              <p className="refuel-total">Сумма: {lineTotal} ₽</p>
            ) : null}
            {splitVisible && splitLocked ? (
              <p className="refuel-split-hint">
                Распределение: {expenseSplitLabel(costSplit, 'fuel')}. Изменить
                может администратор.
              </p>
            ) : null}
            {splitVisible && allowFullSplit ? (
              <>
                <p className="refuel-split-hint">
                  Распределение только этой заправки.
                </p>
                <ExpenseSplitFields
                  date={date}
                  amount={lineTotal}
                  expenseType="fuel"
                  allowedSplits={fuelSplitsForActor(allowFullSplit)}
                  costSplit={costSplit}
                  manualShares={manualShares}
                  flightIds={flightIds}
                  onCostSplitChange={(value) => {
                    setCostSplit(value);
                    if (value === 'personal' && !payerUserId) {
                      setPayerUserId(defaultPayerId);
                    }
                  }}
                  onManualSharesChange={setManualShares}
                  onFlightIdsChange={setFlightIds}
                  radioName="refuel-fuel-split"
                  payerUserId={payerUserId || defaultPayerId}
                  onPayerUserIdChange={setPayerUserId}
                  payerOptions={payerOptions}
                  lockedHourFlight={lockedHourFlight}
                />
              </>
            ) : null}
          </div>
          <div className="modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Отмена
            </button>
            <button type="submit" className="btn-save">
              {initial ? 'Сохранить' : 'Добавить'}
            </button>
          </div>
        </form>
      </ModalOverlay>
    </ModalPortal>
  );
};

export default AddRefuelModal;
